import React from 'react';
import PropTypes from 'prop-types';
import AppGridContainer from '@tenant/core/AppGridContainer';
import { Box, FormControl, FormHelperText, Grid, ListItemText, TextField } from '@mui/material';
import AppGlossary from '@tenant/core/App/Common/AppGlossary';
import CategorySelect from '@tenant/core/App/Select/CategorySelect';
import UploadBox from '@tenant/core/AppInputImage/UploadBox';
import { getBlobFileImage } from 'pages/publishApp/helpers/common';
import PersonasSelect from '@tenant/core/App/Select/PersonasSelect';
import CompanyProductSelect from '@tenant/core/App/Select/CompanyProductSelect';

const GeneralInformationStep = ({ errors = {}, values = {}, touched = {}, setFieldValue = () => {} }) => {
	return (
		<AppGridContainer
			sx={{
				'& .required-sign': {
					color: 'error.main'
				}
			}}
		>
			<Grid item xs={12} sm={8} md={9} lg={10}>
				<Grid container spacing={4}>
					<Grid item xs={12} sm={12}>
						<Box component="p" color="text.primary" fontSize={14} mb={2}>
							Name
							<Box className="required-sign" component="span" ml={1}>
								*
							</Box>
						</Box>
						<TextField
							placeholder="Enter text"
							name="Name"
							error={touched.Name && !!errors.Name}
							helperText={touched.Name ? errors.Name : ''}
							value={values.Name ?? ''}
							onChange={e => {
								setFieldValue('Name', e.target.value);
							}}
							onBlur={e => {
								setFieldValue('Name', values.Name?.trim());
							}}
							fullWidth
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Box component="p" color="text.primary" fontSize={14} mb={2}>
							Key Label <AppGlossary title='A brief label displayed on the app, such as "20% off" or "Free," to highlight offers or key info.' />
						</Box>
						<TextField
							placeholder="Enter text"
							error={!!errors.KeyLabels}
							helperText={errors.KeyLabels}
							value={values.KeyLabels ?? ''}
							onChange={e => {
								setFieldValue('KeyLabels', e.target.value);
							}}
							onBlur={e => {
								setFieldValue('KeyLabels', values.KeyLabels?.trim());
							}}
							fullWidth
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Box component="p" color="text.primary" fontSize={14} mb={2}>
							Dependencies
							<AppGlossary
								title="Define the services or infrastructure that your app relies on.
        List any dependencies that your app needs to operate, such as third-party services, databases, or APIs"
							/>
						</Box>
						<TextField
							placeholder="Enter text"
							error={!!errors.Dependencies}
							helperText={errors.Dependencies}
							value={values.Dependencies ?? ''}
							onChange={async e => {
								setFieldValue('Dependencies', e.target.value);
							}}
							onBlur={e => {
								setFieldValue('Dependencies', values.Dependencies?.trim());
							}}
							fullWidth
						/>
					</Grid>
					<Grid item xs={12} sm={12} md={12}>
						<Box component="p" color="text.primary" fontSize={14} mb={2}>
							Categories
							<Box component="span" ml={1} sx={{ color: 'error.main' }}>
								*
							</Box>
							<AppGlossary
								title="Select the marketplace categories that best describes your app.
          This classification will help users find your app in the appropriate section on the Joblogic marketplace"
							/>
						</Box>
						<FormControl fullWidth>
							<CategorySelect
								value={values.CategoryIds || []}
								error={touched.CategoryIds && !!errors.CategoryIds}
								helperText={touched.CategoryIds ? errors.CategoryIds : ''}
								limitTags={3}
								onChange={(e, value) => {
									setFieldValue('CategoryIds', value);
								}}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={12}>
						<Box component="p" color="text.primary" fontSize={14} mb={2}>
							Company Products
							<AppGlossary title="This indicates which specific Joblogic products your application is designed to work with." />
						</Box>

						<CompanyProductSelect
							isMultiple
							limitTags={3}
							value={values.CompanyProducts || []}
							error={touched.CompanyProducts && !!errors.CompanyProducts}
							helperText={touched.CompanyProducts ? errors.CompanyProducts : ''}
							onChange={(e, value) => {
								setFieldValue('CompanyProducts', value);
							}}
						/>
					</Grid>
					<Grid item xs={12} sm={6} md={6}>
						<Box component="p" color="text.primary" fontSize={14} mb={2}>
							Personas
							<AppGlossary
								title={`
                  A persona is a semi-fictional character created to represent a segment of users or customers.
                  It's a tool commonly used in marketing, design, user experience (UX) research, and product development to help teams understand their target audience and design with their needs, goals, behaviors, and challenges in mind.
                  `}
							/>
						</Box>
						<FormControl fullWidth>
							<PersonasSelect
								value={values.Personas || []}
								error={touched.Personas && !!errors.Personas}
								helperText={touched.Personas ? errors.Personas : ''}
								limitTags={2}
								onChange={(e, value) => {
									setFieldValue('Personas', value);
								}}
							/>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={6}>
						<Box component="p" color="text.primary" fontSize={14} mb={2}>
							Referral Form
							<AppGlossary title="Enter the name or details of the referral form associated with this app. This helps users understand how the app was referred or connected to other systems, forms, or workflows. It can be a form name, link, or a brief description." />
						</Box>
						<TextField
							placeholder="Enter text"
							error={!!errors.ReferralFormUrl}
							helperText={errors.ReferralFormUrl}
							value={values.ReferralFormUrl ?? ''}
							onChange={async e => {
								setFieldValue('ReferralFormUrl', e.target.value);
							}}
							onBlur={e => {
								setFieldValue('ReferralFormUrl', values.ReferralFormUrl?.trim());
							}}
							fullWidth
						/>
					</Grid>

					{!values?.AppId && (
						<>
							<Grid item xs={12} sm={6}>
								<Box component="p" color="text.primary" fontSize={14} mb={2}>
									Author Name
									<Box component="span" ml={1} sx={{ color: 'error.main' }}>
										*
									</Box>
									<AppGlossary
										title="Enter the name of the person or organization responsible for this app.
          This will helps users identify the creator or responsible party for the app. This can be a developer’s name, a team name, or a company name."
									/>
								</Box>
								<TextField
									disabled={!!values?.AppId}
									placeholder="Enter text"
									error={!!errors.AuthorName}
									helperText={errors.AuthorName}
									value={values.AuthorName ?? ''}
									onChange={async e => {
										setFieldValue('AuthorName', e.target.value);
									}}
									onBlur={e => {
										setFieldValue('AuthorName', values.AuthorName?.trim());
									}}
									fullWidth
								/>
							</Grid>
							<Grid item xs={12} sm={6}>
								<Box component="p" color="text.primary" fontSize={14} mb={2}>
									Author Email{' '}
									<Box component="span" ml={1} sx={{ color: 'error.main' }}>
										*
									</Box>
									<AppGlossary
										title="Provide a contact email for the author or organization behind this app.
          This allows users to reach out for support, feedback, or further inquiries related to the app. Use an active, monitored email address to ensure reliable communication"
									/>
								</Box>
								<TextField
									disabled={!!values?.AppId}
									placeholder="Enter text"
									error={!!errors.AuthorEmail}
									helperText={errors.AuthorEmail}
									value={values.AuthorEmail ?? ''}
									onChange={async e => {
										setFieldValue('AuthorEmail', e.target.value);
									}}
									onBlur={e => {
										setFieldValue('AuthorEmail', values.AuthorEmail?.trim());
									}}
									fullWidth
								/>
							</Grid>
						</>
					)}
				</Grid>

				{!!values?.AppId && (
					<Grid
						container
						spacing={4}
						mt={4}
						ml={0}
						mr={0}
						sx={{
							backgroundColor: '#f5f5f5',
							width: '100%',
							borderRadius: 2
						}}
					>
						<Grid item xs={12} sm={6} mt={-2} ml={-1} mb={2}>
							<ListItemText primary="Author Name" secondary={values.AuthorName} />
						</Grid>
						<Grid item xs={12} sm={6} mt={-2} mb={2}>
							<ListItemText primary="Author Email" secondary={values.AuthorEmail} />
						</Grid>
					</Grid>
				)}
			</Grid>
			<Grid item xs={12} sm={4} md={3} lg={2}>
				<Box mt={7}>
					<UploadBox
						defaultFile={values.IconFile}
						appUrl={values.IconBlobReference}
						error={!!errors.IconBlobReference}
						helperText={errors.IconBlobReference}
						onChange={file => {
							if (file) {
								getBlobFileImage(file, blob => {
									setFieldValue('IconBlobReference', blob);
								});
							} else {
								setFieldValue('IconBlobReference', null);
							}
							setFieldValue('IconFile', file);
						}}
						handleClearIcon={() => {
							setFieldValue('IconBlobReference', null);
							setFieldValue('IconFile', null);
						}}
					/>
					{touched.IconBlobReference && <FormHelperText sx={{ ml: 3, mt: 2, color: 'error.main' }}>{errors.IconBlobReference}</FormHelperText>}
				</Box>
			</Grid>
		</AppGridContainer>
	);
};

export default React.memo(GeneralInformationStep);

GeneralInformationStep.propTypes = {
	errors: PropTypes.object,
	values: PropTypes.object,
	touched: PropTypes.object,
	setFieldValue: PropTypes.func
};
